<svelte:options
  customElement={{
    tag: "oc-chip-v1",
    shadow: "none",
    /*                                                         */
    /*         */
    extend: (customElementConstructor) => {
      return class extends customElementConstructor {
        /*                                          */
        static formAssociated = true;

        constructor() {
          super();
          this.host = this;
          this.attachShadow({ mode: "open", delegatesFocus: true });
          /*                                */
          this.internals = this.attachInternals();

          this.setValidity = this.internals.setValidity.bind(this.internals);
        }

        /*                                                            */
        get willValidate() {
          return this.internals.willValidate;
        }

        get validity() {
          return this.internals.validity;
        }

        get validationMessage() {
          return this.internals.validationMessage;
        }

        get form() {
          return this.internals.form;
        }

        /*                             */
        formResetCallback() {
          if (!this.removable) {
            this.checked = this.hasAttribute("checked");
          }
        }
      };
    },
    props: {
      aria: { type: "Object" },
      inverted: { type: "Boolean" },
      size: { type: "String", reflect: true },
      removable: { type: "Boolean" },
      disabled: { type: "Boolean", reflect: true }, /*                                    */
      name: { type: "String", reflect: true }, /*                                    */
      value: { type: "String" },
      checked: { type: "Boolean" }, /*                                     */
      icon: { type: "String" },
      showCheckIcon: { type: "Boolean", attribute: "show-check-icon" },
      singleSelection: { type: "Boolean", attribute: "single-selection" },
    },
  }}
/>

<script lang="ts">
  import { usePropertyChange } from "../../../common/utils/usePropertyChange";
  import type { Props } from "./ChipV1.types";
  import type { CommonProps } from "../../../common/component.types.js";
  import { useAriaAttributes } from "../../../common/utils/aria/useAriaAttributes.js";
  import { applyAttributesToHost } from "../../../common/utils/aria/applyAttributesToHost.js";

  usePropertyChange();

  export let aria: Required<CommonProps>["aria"] = {};
  $: ariaAttributes = useAriaAttributes(aria, { host: ["hidden"] });
  $: applyAttributesToHost(ariaAttributes.host);

  export let inverted: Props["inverted"] = false;
  export let size: Props["size"] = "100";
  export let removable: Props["removable"] = false;
  export let disabled: Props["disabled"] = false;
  export let name: Props["name"] = undefined;
  export let value: Exclude<Props["value"], undefined> = "on";
  export let checked: Exclude<Props["checked"], undefined> = false;
  export let showCheckIcon: Props["showCheckIcon"] = false;
  export let icon: Props["icon"] = undefined;
  $: leftIcon = checked && showCheckIcon ? "check" : icon;

  export let singleSelection: Props["singleSelection"] = false;

  export let internals: ElementInternals;

  export let host: HTMLElement;

  $: internals?.setFormValue(checked ? value : null);

  /*                                         */
  $: role = removable ? "button" : singleSelection ? "radio" : "checkbox";

  $: {
    aria.checked = removable ? undefined : `${checked}`;
    aria.disabled = disabled ? `true` : undefined;
  }

  const onCheckToggle = (event: MouseEvent | KeyboardEvent) => {
    if (disabled) return;

    if (removable) {
      if (("code" in event && ["Space", "Enter"].includes(event.code)) || event.type === "click") {
        host.dispatchEvent(
          new CustomEvent<undefined>("oc-remove", {
            bubbles: true,
          }),
        );
      }
    } else if (("code" in event && event.code === "Space") || event.type === "click") {
      /*                                        */
      if (singleSelection && checked) return;
      checked = !checked;
    }
  };

  /*                      */
  $: if (singleSelection && name && checked && internals) {
    document.querySelectorAll<HTMLOcChipV1Element>(`oc-chip-v1[name="${name}"]`).forEach((chip) => {
      const isSelf = chip.value === value;
      if (!isSelf && chip.checked) {
        chip.checked = false;
      }
    });
  }
</script>

<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<div
  class="chip chip--size-{size}"
  class:chip--inverted={inverted}
  on:click={onCheckToggle}
  on:keydown={onCheckToggle}
  {role}
  tabindex={disabled ? -1 : 0}
  {...ariaAttributes.default}
>
  <!-- Icon Left -->
  {#if leftIcon}
    <oc-icon-v1 class="chip__icon chip__icon--{size}" type={leftIcon} />
  {/if}

  <label class="chip__label">
    <slot />
    {#if removable}
      <span class="chip__aria-remove">entfernen</span>
    {/if}
  </label>

  <!-- Removable Icon Right -->
  {#if removable}
    <oc-icon-v1 class="chip__icon chip__icon--removable chip__icon--{size}" type={"close"} />
  {/if}
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";
  @use "./ChipV1.config" as config;
  @use "../../../common/scss/visually-hidden" as *;
  @use "../../../common/scss/focus.scss";

  :host {
    @include config.oc-chip;
  }

  :host([size="100"]) {
    @include config.oc-chip-size-100;
  }

  :host([size="200"]) {
    @include config.oc-chip-size-200;
  }

  .chip {
    position: relative;
    box-sizing: border-box;

    display: flex;
    justify-content: center;
    align-items: center;

    background-color: tokens.$oc-component-chip-default-background-color;
    color: tokens.$oc-component-chip-text-color;
    cursor: pointer;
    user-select: none;

    @include focus.styles(20px);

    @media (hover: hover) {
      &:hover {
        background-color: tokens.$oc-component-chip-default-background-color-hover;
      }
    }

    &:active {
      background-color: tokens.$oc-component-chip-default-background-color-active;
    }

    .chip__label {
      pointer-events: none;
      user-select: none;
      font-size: tokens.$oc-component-chip-100-font;
    }

    &--size-100 {
      padding: tokens.$oc-component-chip-100-padding-y tokens.$oc-component-chip-100-padding-x;
      border-radius: 20px;
      gap: tokens.$oc-component-chip-100-gap-x;
      font: tokens.$oc-component-chip-100-font;
      height: tokens.$oc-component-chip-100-height;
    }

    &--size-200 {
      padding: tokens.$oc-component-chip-200-padding-y tokens.$oc-component-chip-200-padding-x;
      border-radius: 24px;
      gap: tokens.$oc-component-chip-200-gap-x;
      font: tokens.$oc-component-chip-200-font;
      height: tokens.$oc-component-chip-200-height;

      @include focus.styles(24px);
    }

    &[aria-checked="true"] {
      background-color: tokens.$oc-component-chip-selected-background-color;
      font-weight: tokens.$oc-component-chip-selected-font-weight;

      @media (hover: hover) {
        &:hover {
          background-color: tokens.$oc-component-chip-selected-background-color-hover;
        }
      }

      &:active {
        background-color: tokens.$oc-component-chip-selected-background-color-active;
      }
    }

    &[aria-disabled="true"] {
      background-color: tokens.$oc-component-chip-disabled-background-color;
      color: tokens.$oc-component-chip-disabled-icon-color;
      pointer-events: none;

      &[aria-checked] {
        font-weight: normal;
      }

      .chip__icon {
        fill: tokens.$oc-component-chip-disabled-icon-color;
      }
    }

    &__icon {
      height: tokens.$oc-component-chip-100-icon-size;
      width: tokens.$oc-component-chip-100-icon-size;
      pointer-events: none;

      &--200 {
        align-self: auto;
        height: tokens.$oc-component-chip-200-icon-size;
        width: tokens.$oc-component-chip-200-icon-size;
      }
    }

    &--inverted {
      background-color: tokens.$oc-component-chip-inverted-background-color;

      @media (hover: hover) {
        &:hover {
          background-color: tokens.$oc-component-chip-inverted-background-color-hover;
        }
      }

      &:active {
        background-color: tokens.$oc-component-chip-inverted-background-color-active;
      }
    }

    &__aria-remove {
      @include visually-hidden();
    }
  }
</style>
